import React, { useState } from 'react';
import '../styles/Categorytree.css';
import '../styles/Button.css';
import { Departamento } from '../types/index';

interface CategoryTreeProps {
    categories: Departamento[];
    onConfirm: (type: string, item: any) => void;
    onEdit: (type: string, item: any) => void;
    onAdd: (type: string, parent?: any) => void;
}

interface TreeItem {
    id: number | string;
    nombre: string;
    descripcion?: string;
    type: 'departamento' | 'categoria' | 'subcategoria';
    children?: TreeItem[];
    parent?: TreeItem;
}

const CategoryTree: React.FC<CategoryTreeProps> = ({
    categories,
    onConfirm,
    onEdit,
    onAdd
}) => {
    const [selectedNode, setSelectedNode] = useState<TreeItem | null>(null);
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

    const toggleExpand = (nodeId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };

    const handleSelect = (node: TreeItem) => {
        setSelectedNode(node);
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'departamento': return '🏢';
            case 'categoria': return '📁';
            case 'subcategoria': return '🔖';
            default: return '📄';
        }
    };

    // Convert raw categories to hierarchical TreeItems
    const transformToTree = (depts: Departamento[]): TreeItem[] => {
        if (!depts || !Array.isArray(depts)) return [];

        return depts.map(dept => {
            const deptNode: TreeItem = {
                id: `dept-${dept.id}`,
                nombre: dept.nombre || (dept as any).name || 'Sin nombre',
                type: 'departamento',
                children: []
            };

            // Support multiple possible property names from backend (Sequelize variations)
            const deptCats = (dept as any).Categorias || (dept as any).categorias ||
                (dept as any).CategoriaS || (dept as any).categoriaS ||
                (dept as any).Categoria || (dept as any).categoria ||
                (dept as any).items || [];

            if (Array.isArray(deptCats) && deptCats.length > 0) {
                deptNode.children = deptCats.map((cat: any) => {
                    const catNode: TreeItem = {
                        id: `cat-${cat.id}`,
                        nombre: cat.nombre || cat.name || 'Sin nombre',
                        type: 'categoria',
                        parent: deptNode,
                        children: []
                    };

                    const catSubs = cat.Subcategorias || cat.subcategorias ||
                        cat.SubcategoriaS || cat.subcategoriaS ||
                        cat.Subcategoria || cat.subcategoria ||
                        cat.items || [];

                    if (Array.isArray(catSubs) && catSubs.length > 0) {
                        catNode.children = catSubs.map((sub: any) => ({
                            id: `sub-${sub.id}`,
                            nombre: sub.nombre || sub.name || 'Sin nombre',
                            type: 'subcategoria',
                            parent: catNode
                        }));
                    }
                    return catNode;
                });
            }
            return deptNode;
        });
    };

    const treeData = transformToTree(categories || []);

    const renderTreeNode = (node: TreeItem, depth: number = 0) => {
        const isExpanded = expandedNodes.has(node.id.toString());
        const isSelected = selectedNode?.id === node.id;
        const hasChildren = node.children && node.children.length > 0;

        return (
            <li key={node.id} className="tree-node">
                <div
                    className={`tree-item-content ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => {
                        handleSelect(node);
                        if (hasChildren) {
                            toggleExpand(node.id.toString(), { stopPropagation: () => { } } as any);
                        }
                    }}
                    style={{ paddingLeft: `${depth * 16 + 12}px` }}
                >
                    <span
                        className="tree-expander"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(node.id.toString(), e);
                        }}
                    >
                        {hasChildren ? '▶' : ''}
                    </span>
                    <span className="tree-icon">{getIcon(node.type)}</span>
                    <span className="tree-label">{node.nombre}</span>

                    <div className="tree-node-actions">
                        {node.type !== 'subcategoria' && (
                            <button
                                className="mini-action-btn"
                                title={`Añadir ${node.type === 'departamento' ? 'Categoría' : 'Subcategoría'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const nextType: string = node.type === 'departamento' ? 'categoria' : 'subcategoria';
                                    // We need the original object format for the parent
                                    const parentIdIdx = node.id.toString().split('-')[1] || '0';
                                    const parentId = parseInt(parentIdIdx);
                                    onAdd(nextType, { id: parentId, nombre: node.nombre as string });
                                }}
                            >
                                +
                            </button>
                        )}
                    </div>
                </div>

                {isExpanded && hasChildren && (
                    <ul className="tree-node-list tree-children">
                        {node.children!.map(child => renderTreeNode(child, depth + 1))}
                    </ul>
                )}
            </li>
        );
    };

    const getBreadcrumbs = (node: TreeItem): string[] => {
        const crumbs: string[] = [node.nombre as string];
        let current = node.parent;
        while (current) {
            crumbs.unshift(current.nombre as string);
            current = current.parent;
        }
        return crumbs;
    };

    return (
        <div className="category-tree-wrapper">
            <div className="tree-container">
                <div className="tree-header">
                    <h2>Departamentos</h2>
                    <button
                        className="btn-add-circle"
                        title="Añadir Departamento"
                        onClick={() => onAdd('departamento')}
                    >
                        +
                    </button>
                </div>
                <ul className="tree-node-list">
                    {treeData.map(node => renderTreeNode(node))}
                </ul>
            </div>

            <div className="data-container detail-container">
                {selectedNode ? (
                    <>
                        <div className="detail-header">
                            <div className="breadcrumbs">
                                {getBreadcrumbs(selectedNode).map((crumb, i) => (
                                    <span key={i} className="breadcrumb-item">{crumb}</span>
                                ))}
                            </div>
                            <h3 className="detail-title">{selectedNode.nombre}</h3>
                        </div>

                        <div className="detail-body">
                            <div className="info-card">
                                <strong>Tipo:</strong> {selectedNode.type.charAt(0).toUpperCase() + selectedNode.type.slice(1)}
                                <br />
                                <strong>ID:</strong> {selectedNode.id.toString().split('-')[1]}
                            </div>

                            {selectedNode.descripcion && (
                                <p style={{ marginTop: '1.5rem' }}>
                                    {selectedNode.descripcion}
                                </p>
                            )}

                            {selectedNode.children && selectedNode.children.length > 0 && (
                                <div style={{ marginTop: '1.5rem' }}>
                                    <strong>Contiene:</strong> {selectedNode.children.length} {selectedNode.type === 'departamento' ? 'Categorías' : 'Subcategorías'}
                                </div>
                            )}
                        </div>

                        <div className="button-group detail-actions">
                            <button
                                className="button primary"
                                onClick={() => {
                                    if (selectedNode) {
                                        const rawIdIdx = selectedNode.id.toString().split('-')[1] || '0';
                                        const rawId = parseInt(rawIdIdx);
                                        onEdit(selectedNode.type as string, { ...selectedNode, id: rawId });
                                    }
                                }}
                            >
                                Editar Details
                            </button>
                            <button
                                className="button danger"
                                onClick={() => {
                                    if (selectedNode) {
                                        const rawIdIdx = selectedNode.id.toString().split('-')[1] || '0';
                                        const rawId = parseInt(rawIdIdx);
                                        onConfirm(selectedNode.type as string, { ...selectedNode, id: rawId });
                                    }
                                }}
                            >
                                Eliminar
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="detail-empty">
                        <span>🌳</span>
                        <p>Selecciona un elemento del árbol para ver o editar sus detalles.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CategoryTree;
